import { useTenantData } from '@dev-agents/tenant-context';
import { graphql, useMutation } from 'react-relay';
import type { RecordSourceProxy } from 'relay-runtime';

import { useAnalyticsWebClient } from '../../../../utils/analytics/context';
import { useAuthData } from '../../../../utils/auth-context';

import type {
	useCompleteFlowMutation,
	useCompleteFlowMutation$data,
} from './__generated__/useCompleteFlowMutation.graphql';

export const useCompleteFlow = (): [(id: string, flowARI: string) => void, boolean] => {
	const analyticsWebClient = useAnalyticsWebClient();
	const { user } = useAuthData();
	const { tenantContexts } = useTenantData();
	const { cloudId } = tenantContexts[0];

	const [commit, isPending] = useMutation<useCompleteFlowMutation>(graphql`
		mutation useCompleteFlowMutation($id: ID!) {
			devai_flowSessionComplete(id: $id) @optIn(to: "DevAiFlowSessionComplete") {
				id
				sessionARI
				status
			}
		}
	`);

	const completeSession = (id: string, flowARI: string) => {
		const updater = (store: RecordSourceProxy, response?: useCompleteFlowMutation$data | null) => {
			if (response?.devai_flowSessionComplete?.status !== 'COMPLETED') {
				return;
			}

			const root = store.getRoot();
			const sessions = root.getLinkedRecords('devai_flowSessionsByCreatorAndCloudId', {
				creator: user?.accountId,
				cloudId,
			});

			if (sessions) {
				// Filter out the completed session
				const updatedSessions = sessions.filter((session) => session?.getValue('id') !== id);

				// Update the connection with the filtered list
				root.setLinkedRecords(updatedSessions, 'devai_flowSessionsByCreatorAndCloudId', {
					creator: user?.accountId,
					cloudId,
				});
			}
		};

		const trackSuccessEvent = () => {
			analyticsWebClient.sendOperationalEvent({
				action: 'success',
				actionSubject: 'complete-flow',
				source: 'use-complete-flow',
				attributes: {
					flowARI,
				},
			});
		};

		const trackFailedEvent = (error: Error) => {
			analyticsWebClient.sendOperationalEvent({
				action: 'failed',
				actionSubject: 'complete-flow',
				source: 'use-complete-flow',
				attributes: {
					flowARI,
					error: error.message,
				},
			});
		};

		commit({
			variables: {
				id: flowARI,
			},
			optimisticResponse: {
				devai_flowSessionComplete: {
					id,
					sessionARI: flowARI,
					status: 'COMPLETED',
				},
			},
			optimisticUpdater: updater,
			updater,
			onCompleted: (response) => {
				if (response?.devai_flowSessionComplete?.status === 'COMPLETED') {
					trackSuccessEvent();
				} else {
					trackFailedEvent(new Error("Couldn't complete flow"));
				}
			},
			onError: (error) => {
				trackFailedEvent(error);
			},
		});
	};

	return [completeSession, isPending];
};
